/**
 * The workspace's words for the recording surface, written once.
 *
 * "Video submissions" is a **display rename and nothing else**. Every identifier
 * underneath keeps the name it was stored under — the `interviews` subcollection,
 * the `interviews/` API prefix, the `/interview/:workTrialId/:interviewId` route,
 * the `video_files` keys and the `interview-videos/…` GCS prefix — because renaming
 * a stored key orphans records and breaks links that are already in people's
 * inboxes. So the rename happens at the reading, in one place, and this is that
 * place: the rail row, the screen's title, the chips on its calendar block and the
 * copy a decision is announced in all read from here, which is what makes the
 * wording a one-line change rather than a search-and-replace.
 *
 * The second half of the file is the same rename carried into the recording
 * session itself. `components/InterviewSession.js` and its five children are shared
 * with `pages/PrescreeningInterview.js` and `pages/ProfileInterview.js`, which are
 * honest Appli product UI on the far side of the two-worlds boundary and are
 * genuinely interviews. They must not change a word. So the session takes a
 * `labels` object whose defaults are today's exact strings, `pages/Interview.js`
 * passes this override set — and only for a trial whose engine is eval4 — and the
 * two Appli-side pages pass nothing at all.
 *
 * **The rule the override set is built to.** A default string is given a label key
 * when it carries a word the rename replaces (`/\b(interview|check-?in|debrief)s?\b/i`)
 * or, in the small handful of cases marked below, a word that would break the
 * workspace's fiction. `src/tests/workspace/videoSubmissionLabels.test.js` asserts
 * both directions: every default that matches gets an override, and no override
 * matches. A new default string carrying the old noun therefore fails a test
 * rather than reaching a candidate.
 */

/**
 * The surface's name. One constant, four readers: the rail row
 * (`components/workspace/workspacePages.js`), the screen's own title (which reads
 * the rail's table, so it can never be called two things), the chips on the
 * screen's calendar block, and the sentence a request decision is announced in
 * (`api/workspaceRequests.js`, `pages/workspace/screens/Tasks.js`).
 *
 * `inline` is the same noun mid-sentence rather than a `toLowerCase()` at each
 * call: a name whose casing is decided in four places is a name that will be
 * capitalised differently in four places.
 */
export const VIDEO_SUBMISSIONS = Object.freeze({
    /** The surface: the rail row and the screen's heading. */
    page: 'Video submissions',
    /** One of them, at the start of a sentence or as a heading. */
    one: 'Video submission',
    /** One of them, inside a sentence. */
    inline: 'video submission',
    /** One the payload gave no name to. Numbered, never left blank. */
    nth: (index) => `Video submission ${index}`,
});

/** The Handbook's name: the rail row, the screen's heading and the way back to it. */
export const HANDBOOK = 'Handbook & Lessons';

/**
 * The recording session, in the workspace's words.
 *
 * Keys mirror `INTERVIEW_SESSION_LABELS` in `components/InterviewSession.js`, plus
 * one group per child component that carries copy of its own. A key absent here
 * keeps the component's default, which is why this object is short: most of the
 * session's copy — device names, timers, question counts, the one-take rules — says
 * the same thing in either world and is left exactly as it is.
 */
export const VIDEO_SESSION_LABELS = Object.freeze({
    loading: 'Loading your video submission...',
    notFound: 'Video submission not found',
    accessDenied: 'You do not have access to this video submission',
    loadFailed: 'Failed to load this video submission',
    leaveConfirm:
        'Leave this recording? Your in-progress answer will be lost and cannot be recovered, and the question timer will keep running.',
    tagline: 'Record a video answer to each question',
    welcome: (type) => `Welcome to your ${String(type).toLowerCase()}`,
    // Not the rename: "assessment" is the one word on this page that would tell a
    // candidate they are being marked rather than doing a piece of work.
    videoRuleBody: 'Your answers are recorded with both video and audio, and are sent exactly as recorded.',
    permissionsBody:
        'To record smoothly, we need access to your camera and microphone. Please make sure they are properly connected and working.',
    timersBody:
        'Closing or reloading this page does not end your recording — you can return and continue from your next unanswered question. But the timer on an open question keeps running while you are away, and an answer you have not submitted is lost. Any question not submitted within its time window is forfeited. Stay on this page until you finish.',
    loggingBody:
        'While a question is being recorded, this page logs tab and window focus changes, your overall keyboard and mouse activity levels — never what you type — and camera or microphone changes. These signals are stored with your recording.',
    acceptHint: 'Please accept the conditions above to begin recording.',
    beginButton: 'Begin Recording',
    practiceIntroBody:
        "Before you record for real, let's do a quick practice question to help you get comfortable with the recording process and verify your devices work properly.",
    practiceNoteTail: 'to check how your devices sound and look before you record for real.',
    practiceReplayBody:
        'Watch your practice recording below to check your video quality, audio clarity, and lighting. Make sure you can see and hear yourself clearly before you record for real.',
    practiceTranscriptEmpty: 'Your transcript will show up when you record for real!',
    practiceContinue: 'Looks Good — Start Recording',
    submitTitle: 'Send your video submission',
    submittingTitle: 'Sending your video submission…',
    submitButton: 'Send Video Submission',
    submittingAria: 'Sending your video submission',
    submittedHeading: 'Video submission sent',
    endExpired:
        'Your session has expired. Your answers are saved — log in again to send your video submission.',
    endFailed:
        'Your video submission could not be sent — your answers are saved and nothing is lost. Check your connection and retry.',
    // A prefix the session concatenates onto, so the trailing space is load-bearing.
    mediaRequired: 'Camera and microphone access is required to record. ',
    startFailed: 'Failed to start recording. Please try again.',
    startExpired: 'Your session has expired. Log in again to start recording.',

    /* The four children that carry copy of their own. `MediaPlaceholder` is the
       fifth and takes none: every caption it draws is handed to it by the session. */
    stepper: Object.freeze({
        steps: Object.freeze(['Setup', 'Rules', 'Practice', 'Recording']),
        progress: 'Recording progress',
    }),
    resume: Object.freeze({
        eyebrow: 'Recording interrupted',
        heading: 'Your recording was interrupted',
        lede: 'This page was left, reloaded, or closed while your recording was in progress.',
    }),
    // `DeviceCheckPanel` and `InterviewAlert` say the same thing in either world —
    // a camera is a camera — so the seam is threaded and left unused rather than
    // given a difference it does not have.
});

/**
 * Where the session sends a candidate back to. The workspace, never the legacy
 * work-trial page: a candidate who reached the recording from their workspace has
 * no other page in this world to be returned to.
 * @param {string} workTrialId
 * @returns {string}
 */
export const videoSessionPath = (workTrialId) => `/workspace/${workTrialId}/video`;

/**
 * The route one video submission is recorded at.
 *
 * Spelled here beside the rename so the one place the old noun survives in a URL
 * is the one place it is allowed to: this is the live link, in emails and in
 * records, and it does not move.
 * @param {string} workTrialId
 * @param {string} videoSubmissionId
 * @returns {string}
 */
export const videoRecordPath = (workTrialId, videoSubmissionId) =>
    `/interview/${workTrialId}/${videoSubmissionId}`;
